<template>
  <transition name="product-stickybasket">
    <div
         v-if="isStickyVisible"
         :class="['product-stickybasket', { 'is-mobile': $root.isMobile, 'is-desktop': !$root.isMobile }]"
         :style="containerStyles">
      <div class="container">
        <div class="product-stickybasket-inner">
          <div class="product-stickybasket-image">
            <c-fallback-image>
              <img v-if="activeImageUrl" :src="activeImageUrl" :alt="activeTitle1 || activeModel" loading="lazy">
            </c-fallback-image>
          </div>

          <div class="product-stickybasket-model-col">
            <p class="product-stickybasket-model mb-0">{{ activeModel }}</p>
          </div>

          <div class="product-stickybasket-title-col">
            <p class="product-stickybasket-title1 mb-0">{{ activeTitle1 }}</p>
            <p class="product-stickybasket-title2 mb-0">{{ activeTitle2 || '\u00a0' }}</p>
          </div>

          <div class="product-stickybasket-text-mobile">
            <p class="product-stickybasket-model mb-0">{{ activeModel }}</p>
            <p class="product-stickybasket-title1 mb-0">{{ activeTitle1 }}</p>
            <p class="product-stickybasket-title2 mb-0">{{ activeTitle2 || '\u00a0' }}</p>
          </div>

          <div class="product-stickybasket-price">
            <c-product-price
                             :product-id="activeProductId"
                             :priceinfo="activePriceinfo"
                             :enable-accessory-total="true"
                             inherit />
          </div>

          <div class="product-stickybasket-action">
            <c-product-shoppingcart-button
                                           :product-id="activeProductId"
                                           :quantityinfo="quantityinfo"
                                           :translations="translations"
                                           :translations-once="true"
                                           :enable-accessory-bundle="true"
                                           :small="$root.isMobile"
                                           :dropup="$root.isMobile"
                                           gtm-list="Product Detail Page">
              <lazy-icon v-if="$root.isMobile" icon="cart" />
              <span v-else>{{ buttonLabel }}</span>
            </c-product-shoppingcart-button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import debounce from 'lodash/debounce';

export default {
  name: 'ProductStickyBasket',
  props: {
    productId: {
      type: String,
      required: true
    },
    model: {
      type: String,
      default: ''
    },
    imageUrl: {
      type: String,
      default: ''
    },
    title1: {
      type: String,
      default: ''
    },
    title2: {
      type: String,
      default: ''
    },
    buttonLabel: {
      type: String,
      default: ''
    },
    priceinfo: {
      type: Object,
      required: true
    },
    quantityinfo: {
      type: Object,
      default: () => ({})
    },
    translations: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      observer: null,
      mutationObserver: null,
      isStickyVisible: false,
      targetButton: null,
      rootMargin: '',
      onViewportUpdateDebounced: null,
      overrides: {}
    }
  },
  computed: {
    headerOffset() {
      if (this.$root.isMobile) {
        return 0
      }

      return Math.max(0, Math.round(this.$root.header?.inViewportHeight || this.$root.header?.height || 0))
    },
    containerStyles() {
      if (this.$root.isMobile) {
        return null
      }

      return {
        top: `${this.headerOffset}px`
      }
    },
    activeProductId() {
      return this.overrides.productId ?? this.productId
    },
    activeModel() {
      return this.overrides.model ?? this.model
    },
    activeImageUrl() {
      return this.overrides.imageUrl ?? this.imageUrl
    },
    activeTitle1() {
      return this.overrides.title1 ?? this.title1
    },
    activeTitle2() {
      return this.overrides.title2 ?? this.title2
    },
    activePriceinfo() {
      return this.overrides.priceinfo ?? this.priceinfo
    }
  },
  methods: {
    onStickyBasketUpdate(data) {
      this.overrides = data
    },
    findTargetButton() {
      return document.querySelector('.product-actions .product-shoppingcartbutton')
    },
    setTargetButton() {
      this.targetButton = this.findTargetButton()
    },
    syncTargetButton() {
      const nextTargetButton = this.findTargetButton()

      if (nextTargetButton === this.targetButton) {
        return
      }

      this.destroyObserver()
      this.targetButton = nextTargetButton

      if (this.targetButton) {
        this.createObserver()
      }
    },
    getRootMargin() {
      if (this.$root.isMobile) {
        return '0px 0px 0px 0px'
      }

      return `-${this.headerOffset}px 0px 0px 0px`
    },
    createObserver() {
      if (!this.targetButton || typeof window.IntersectionObserver === 'undefined') {
        return
      }

      this.rootMargin = this.getRootMargin()
      this.observer = new IntersectionObserver(this.onIntersection, {
        root: null,
        rootMargin: this.rootMargin,
        threshold: 0
      })

      this.observer.observe(this.targetButton)
      this.updateStickyVisibility()
    },
    reconnectObserverIfNeeded() {
      if (!this.observer || !this.targetButton) {
        return
      }

      const nextRootMargin = this.getRootMargin()
      if (nextRootMargin === this.rootMargin) {
        return
      }

      this.observer.disconnect()
      this.observer = null
      this.createObserver()
    },
    onIntersection() {
      this.updateStickyVisibility()
    },
    updateStickyVisibility() {
      if (!this.targetButton || !this.targetButton.isConnected) {
        this.syncTargetButton()
      }

      if (!this.targetButton) {
        this.isStickyVisible = false
        return
      }

      const buttonBounds = this.targetButton.getBoundingClientRect()
      const viewportTop = this.$root.isMobile ? 0 : this.headerOffset
      const viewportBottom = window.innerHeight

      const isButtonVisible = buttonBounds.bottom > viewportTop && buttonBounds.top < viewportBottom
      this.isStickyVisible = !isButtonVisible
    },
    onViewportUpdate() {
      this.syncTargetButton()

      this.reconnectObserverIfNeeded()
      this.updateStickyVisibility()
    },
    onDomMutation() {
      this.onViewportUpdate()
    },
    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    destroyMutationObserver() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect()
        this.mutationObserver = null
      }
    }
  },
  created() {
    this.$root.$on('updateStickyBasket', this.onStickyBasketUpdate)
  },
  mounted() {
    this.onViewportUpdateDebounced = debounce(this.onViewportUpdate, 80)

    this.$nextTick(() => {
      this.setTargetButton()
      this.createObserver()
      this.updateStickyVisibility()
    })

    window.addEventListener('resize', this.onViewportUpdateDebounced)
    window.addEventListener('scroll', this.onViewportUpdateDebounced)

    this.mutationObserver = new MutationObserver(this.onDomMutation)
    this.mutationObserver.observe(document.body, { childList: true, subtree: true })
  },
  beforeDestroy() {
    this.$root.$off('updateStickyBasket', this.onStickyBasketUpdate)
    this.destroyObserver()
    this.destroyMutationObserver()

    if (this.onViewportUpdateDebounced) {
      this.onViewportUpdateDebounced.cancel()
    }

    window.removeEventListener('resize', this.onViewportUpdateDebounced)
    window.removeEventListener('scroll', this.onViewportUpdateDebounced)
  }
}
</script>

<style lang="scss">
$product-stickybasket-zindex: $zindex-sticky + 4 !default;
$product-stickybasket-bg: $white !default;
$product-stickybasket-border: map-get($borders, 'base') !default;
$product-stickybasket-shadow: $box-shadow-sm !default;
$product-stickybasket-padding-y: $spacer * 0.55 !default;
$product-stickybasket-transition: 200ms ease !default;
$product-stickybasket-row-height: 100px !default;

.product-stickybasket {
  position: fixed;
  left: 0;
  right: 0;
  z-index: $product-stickybasket-zindex;
  background-color: $product-stickybasket-bg;
  border-bottom: $product-stickybasket-border;
  box-shadow: $product-stickybasket-shadow;

  &.is-mobile {
    top: auto;
    bottom: 0;
    border-top: $product-stickybasket-border;
    border-bottom: 0 none;
  }

  .product-stickybasket-inner {
    display: grid;
    grid-template-columns: minmax(96px, 128px) auto minmax(0, 1fr) auto 25%;
    align-items: center;
    gap: $spacer;
    padding-top: $product-stickybasket-padding-y;
    padding-bottom: $product-stickybasket-padding-y;
    min-height: $product-stickybasket-row-height;
  }

  .product-stickybasket-image {
    width: 100%;
    max-width: 128px;
    aspect-ratio: 4 / 3;
    border-radius: $border-radius-sm;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    .fallback-image,
    .is-fallback-image {
      width: 100%;
      height: 100%;
    }
  }

  .product-stickybasket-content {
    min-width: 0;
  }

  .product-stickybasket-model,
  .product-stickybasket-title1,
  .product-stickybasket-title2 {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .product-stickybasket-model-col,
  .product-stickybasket-title-col,
  .product-stickybasket-price {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .product-stickybasket-model {
    font-weight: $font-weight-bold;
    text-transform: uppercase;
    font-size: $font-size-base;
    color: $gray-800;
  }

  .product-stickybasket-title-col {
    display: grid;
    align-content: center;
    gap: $spacer * 0.1;
    min-width: 0;
    min-height: calc(#{$font-size-base} * 2.5);
  }

  .product-stickybasket-title1 {
    font-weight: $font-weight-medium;
    font-size: $font-size-base;
    line-height: 1.25;
    color: $gray-800;
  }

  .product-stickybasket-title2 {
    font-size: $font-size-sm;
    color: $gray-600;
    line-height: 1.25;
  }

  .product-stickybasket-text-mobile {
    display: none;
  }

  .product-stickybasket-price {
    .product-price {
      line-height: 1.05;
      white-space: normal;
    }
  }

  .product-stickybasket-action {
    width: 100%;
    min-width: 0;

    .product-shoppingcartbutton {
      .shoppingcartbutton {
        min-height: 52px;
        justify-content: space-between;

        .shoppingcartbutton-text {
          display: flex;
          justify-content: center;
          align-items: center;
          flex: 1 1 auto;
          text-align: center;
          white-space: nowrap;
        }
      }
    }
  }
}

.product-stickybasket-enter-active,
.product-stickybasket-leave-active {
  transition: transform $product-stickybasket-transition, opacity $product-stickybasket-transition;
}

.product-stickybasket-enter,
.product-stickybasket-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.product-stickybasket.is-mobile.product-stickybasket-enter,
.product-stickybasket.is-mobile.product-stickybasket-leave-to {
  transform: translateY(8px);
}

@include media-breakpoint-down($desktop-breakpoint - 1) {
  .product-stickybasket {
    &.is-mobile {
      .product-stickybasket-inner {
        grid-template-columns: minmax(0, 1fr) auto auto;
        gap: $spacer * 0.5;
        min-height: 84px;
      }

      .product-stickybasket-model-col,
      .product-stickybasket-title-col {
        display: none;
      }

      .product-stickybasket-text-mobile {
        display: grid;
        align-content: center;
        min-width: 0;
        gap: $spacer * 0.08;
      }

      .product-stickybasket-model {
        font-size: $font-size-sm;
        line-height: 1.15;
      }

      .product-stickybasket-title1,
      .product-stickybasket-title2 {
        line-height: 1.15;
      }

      .product-stickybasket-title1 {
        font-size: $font-size-sm;
      }

      .product-stickybasket-title2 {
        font-size: $font-size-sm;
      }

      .product-stickybasket-price {
        justify-content: flex-end;
        padding-right: $spacer * 0.25;

        .product-price {
          .row {
            flex-direction: column;
            align-items: flex-end;
          }

          .price-original {
            margin-left: 0;
            margin-top: $spacer * 0.1;
          }
        }

        .price-vatinfo {
          display: none;
        }
      }

      .product-stickybasket-image {
        display: none;
      }

      .product-stickybasket-action {
        min-width: 0;
        justify-self: end;

        .product-shoppingcartbutton .shoppingcartbutton .shoppingcartbutton-text {
          padding-left: $spacer * 0.35;
          padding-right: $spacer * 0.35;
          min-width: $spacer * 1.1;
          text-align: center;
        }
      }
    }
  }
}
</style>
